import PropTypes from 'prop-types'
import styled, { theme } from '@nobia/zeus-components/lib/styled'
import React, { useState, useEffect, useRef, useCallback } from 'react'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const Input = styled.input`
  border-radius: 3px;
  border: ${theme('email.input.border')};
  padding: 15px;
  font-size: 16px;
  width: 100%;
  color: ${theme('colors.primary')};
  background-color: ${theme('email.input.bgColor')};
`

const Dropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${theme('colors.white')};
  border: ${theme('email.input.border')};
  border-top: none;
  border-radius: 0 0 3px 3px;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const Item = styled.li`
  padding: 12px 15px;
  cursor: pointer;
  font-size: 16px;
  color: ${theme('colors.primary')};
  background-color: ${(p) => (p.active ? '#f5f5f5' : 'transparent')};

  &:hover {
    background-color: #f5f5f5;
  }
`

const LocationAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder,
  countryCodes = ['gb'],
}) => {
  const [results, setResults] = useState([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false)
  const sessionTokenRef = useRef(null)

  const apiKey = window?.googleMapsApiKey
  const hasGoogleMaps = window.google?.maps?.places

  useEffect(() => {
    if (isGoogleMapsLoaded || !apiKey) {
      return
    }

    if (hasGoogleMaps) {
      setIsGoogleMapsLoaded(true)
      return
    }

    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    )

    if (existingScript) {
      return
    }

    // Create and load script
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
    script.async = true
    script.onload = () => setIsGoogleMapsLoaded(true)
    document.head.appendChild(script)
  }, [isGoogleMapsLoaded, apiKey, hasGoogleMaps])

  const getSessionToken = useCallback(() => {
    if (!sessionTokenRef.current && hasGoogleMaps) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken()
    }
    return sessionTokenRef.current
  }, [hasGoogleMaps])

  const clearResults = useCallback(() => {
    setResults([])
    setHighlightedIndex(-1)
  }, [])

  const fetchPredictions = useCallback(
    (input) => {
      if (!hasGoogleMaps || input.length < 3) {
        clearResults()
        return
      }

      const service = new window.google.maps.places.AutocompleteService()
      service.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: countryCodes },
          sessionToken: getSessionToken(),
        },
        (predictions, status) => {
          const isSuccess =
            status === window.google.maps.places.PlacesServiceStatus.OK
          let predictionsResults = []

          if (isSuccess && predictions) {
            predictionsResults = predictions.map((p) => ({
              placeId: p.place_id,
              name: p.description,
            }))
          }

          setResults(predictionsResults)
          setHighlightedIndex(-1)
        }
      )
    },
    [countryCodes, getSessionToken, hasGoogleMaps, clearResults]
  )

  const handleChange = (e) => {
    const newValue = e.target.value
    onChange(newValue)
    fetchPredictions(newValue)
  }

  const handleSelect = (item) => {
    onChange(item?.name)
    clearResults()
    sessionTokenRef.current = null
    onSelect?.(item)
  }

  const handleKeyDown = (e) => {
    if (results.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => Math.min(prev + 1, results.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => Math.max(prev - 1, -1))
        break
      case 'Enter': {
        e.preventDefault()
        const index = highlightedIndex >= 0 ? highlightedIndex : 0
        handleSelect(results[index])
        break
      }
      default:
        break
    }
  }

  // Delay to allow click events to fire before closing dropdown
  const handleBlur = () => {
    setTimeout(clearResults, 200)
  }

  return (
    <Wrapper>
      <Input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={!isGoogleMapsLoaded}
        placeholder={placeholder}
      />
      {results.length > 0 && (
        <Dropdown>
          {results.map((item, index) => (
            <Item
              key={item.placeId}
              active={index === highlightedIndex}
              onClick={() => handleSelect(item)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {item.name}
            </Item>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  )
}

LocationAutocomplete.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  countryCodes: PropTypes.arrayOf(PropTypes.string),
}

export default LocationAutocomplete
